import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // Pre-hydrate from localStorage so a page reload during zoom/scroll on
  // mobile doesn't flash the user back to /home. If we have a cached user
  // AND a token, we skip the loading state and verify in the background.
  const cachedUser = (() => {
    try {
      const raw = localStorage.getItem("app_user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("app_token");
  const [user, setUser] = useState(hasToken ? cachedUser : null);
  const [loading, setLoading] = useState(!(hasToken && cachedUser));
  const [billing, setBilling] = useState(null);

  const persistUser = (u) => {
    setUser(u);
    try {
      if (u) localStorage.setItem("app_user", JSON.stringify(u));
      else localStorage.removeItem("app_user");
    } catch (e) {
      // localStorage can throw in Safari Private mode or when quota is full --
      // the app still works, we just lose the pre-hydration cache on reload.
      console.debug("persistUser localStorage unavailable:", e?.message);
    }
  };

  const refreshBilling = useCallback(async () => {
    try {
      const r = await api.get("/billing/status");
      setBilling(r.data);
      return r.data;
    } catch {
      setBilling(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("app_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        persistUser(r.data);
        if (r.data?.role !== "superadmin") await refreshBilling();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("app_token");
          localStorage.removeItem("app_user");
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, [refreshBilling]);

  // Listen for 401s coming from the api interceptor so we can transition
  // state (user -> null) without a hard window.location navigation.
  useEffect(() => {
    const handler = () => {
      try { localStorage.removeItem("app_user"); } catch (e) {
        console.debug("auth-expired cleanup failed:", e?.message);
      }
      setUser(null);
    };
    window.addEventListener("auth-expired", handler);
    return () => window.removeEventListener("auth-expired", handler);
  }, []);

  const login = async (username, password) => {
    const r = await api.post("/auth/login", { username, password });
    localStorage.setItem("app_token", r.data.token);
    persistUser(r.data.user);
    if (r.data.user?.role !== "superadmin") await refreshBilling();
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("app_token");
    localStorage.removeItem("app_user");
    setUser(null);
    setBilling(null);
  };

  const togglePayrollMode = () => {
    // Payroll Mode was removed; keep a no-op stub so any old caller doesn't crash.
  };

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      persistUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, billing, refreshBilling, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
